import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";

const inputCls = "w-full h-11 px-4 bg-[#0F1115] border border-[#272B33] rounded-xl text-sm text-white focus:outline-none focus:border-blue-500";
const labelCls = "block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5";

const EMPTY = {
  name: "", product_code: "", sku: "", barcode: "", description: "", short_description: "",
  images: [], brand_id: "", category_id: "", subcategory_id: "", tags: [],
  purchase_price: "", price: "", sale_price: "", vat_rate: 20, stock: 0, critical_stock: 5,
  desi: "", weight: "", active: true, has_variants: false, variant_options: [],
  is_featured: false, is_new: false, requires_quote: false, customizable: false, seo_title: "", seo_description: "", seo_keywords: "",
};

export default function ProductFormPage() {
  const { productId } = useParams();
  const isNew = !productId;
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [imagesText, setImagesText] = useState("");
  const [tagsText, setTagsText] = useState("");
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [variants, setVariants] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/categories").then(({ data }) => setCategories(data));
    api.get("/admin/brands").then(({ data }) => setBrands(data));
    if (!isNew) {
      api.get(`/admin/products/${productId}`).then(({ data }) => {
        const { variants: v, ...rest } = data;
        setForm({ ...EMPTY, ...rest, purchase_price: rest.purchase_price ?? "", sale_price: rest.sale_price ?? "", desi: rest.desi ?? "", weight: rest.weight ?? "" });
        setImagesText((data.images || []).join("\n"));
        setTagsText((data.tags || []).join(", "));
        setVariants(v || []);
      }).catch(() => navigate("/admin/urunler"));
    }
  }, [productId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setOptionValues = (idx, valuesText) => {
    const opts = [...form.variant_options];
    opts[idx] = { ...opts[idx], values: valuesText.split(",").map((s) => s.trim()).filter(Boolean) };
    set("variant_options", opts);
  };

  const generateMatrix = () => {
    const opts = form.variant_options.filter((o) => o.name && o.values.length);
    if (!opts.length) { toast.error("Önce varyant tipi ve değerleri girin"); return; }
    let combos = [{}];
    for (const opt of opts) {
      const next = [];
      for (const c of combos) for (const v of opt.values) next.push({ ...c, [opt.name]: v });
      combos = next;
    }
    const merged = combos.map((combo) => {
      const existing = variants.find((v) => JSON.stringify(v.option_values) === JSON.stringify(combo));
      return existing || { option_values: combo, sku: "", barcode: "", price: "", sale_price: "", stock: 0, active: true };
    });
    setVariants(merged);
    toast.success(`${merged.length} varyant kombinasyonu hazır`);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        sale_price: form.sale_price === "" || form.sale_price === null ? null : parseFloat(form.sale_price),
        purchase_price: form.purchase_price === "" ? null : parseFloat(form.purchase_price),
        vat_rate: parseFloat(form.vat_rate) || 0,
        stock: parseInt(form.stock) || 0,
        critical_stock: parseInt(form.critical_stock) || 0,
        safety_stock: parseInt(form.safety_stock) || 0,
        desi: form.desi === "" ? null : parseFloat(form.desi),
        weight: form.weight === "" ? null : parseFloat(form.weight),
        images: imagesText.split("\n").map((s) => s.trim()).filter(Boolean),
        tags: tagsText.split(",").map((s) => s.trim()).filter(Boolean),
        category_id: form.category_id || null,
        subcategory_id: form.subcategory_id || null,
        brand_id: form.brand_id || null,
      };
      let id = productId;
      if (isNew) {
        const { data } = await api.post("/admin/products", payload);
        id = data.product_id;
      } else {
        await api.put(`/admin/products/${productId}`, payload);
      }
      for (const v of variants) {
        const vPayload = {
          option_values: v.option_values, sku: v.sku || null, barcode: v.barcode || null,
          price: v.price === "" || v.price === null || v.price === undefined ? null : parseFloat(v.price),
          sale_price: v.sale_price === "" || v.sale_price === null || v.sale_price === undefined ? null : parseFloat(v.sale_price),
          purchase_price: v.purchase_price === "" || v.purchase_price == null ? null : parseFloat(v.purchase_price),
          vat_rate: v.vat_rate === "" || v.vat_rate == null ? null : parseFloat(v.vat_rate),
          weight: v.weight === "" || v.weight == null ? null : parseFloat(v.weight),
          desi: v.desi === "" || v.desi == null ? null : parseFloat(v.desi),
          stock: parseInt(v.stock) || 0,
          low_stock_threshold: v.low_stock_threshold === "" || v.low_stock_threshold == null ? null : parseInt(v.low_stock_threshold),
          image_url: v.image_url || null,
          images: Array.isArray(v.images) ? v.images : (v.image_url ? [v.image_url] : []),
          active: !!v.active,
        };
        if (v.variant_id) {
          await api.put(`/admin/variants/${v.variant_id}`, vPayload);
        } else if (payload.has_variants) {
          await api.post(`/admin/products/${id}/variants`, vPayload);
        }
      }
      toast.success("Ürün kaydedildi");
      navigate("/admin/urunler");
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  const roots = categories.filter((c) => !c.parent_id);
  const subs = categories.filter((c) => c.parent_id === form.category_id);

  return (
    <form onSubmit={save} className="max-w-5xl space-y-6" data-testid="product-form">
      <div className="flex items-center gap-4">
        <button type="button" onClick={() => navigate("/admin/urunler")} className="p-2 rounded-lg text-neutral-400 hover:bg-[#181B20]" data-testid="product-form-back"><ArrowLeft size={19} /></button>
        <h1 className="font-display font-extrabold text-2xl text-white">{isNew ? "Yeni Ürün" : `Düzenle: ${form.name}`}</h1>
        <button type="submit" disabled={busy} className="ml-auto h-10 px-6 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 disabled:opacity-50" data-testid="product-form-save">
          {busy ? "Kaydediliyor..." : "Kaydet"}
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-[#181B20] border border-[#272B33] rounded-2xl p-6 space-y-4">
            <div><label className={labelCls}>Ürün Adı *</label>
              <input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="pf-name" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className={labelCls}>SKU</label><input value={form.sku || ""} onChange={(e) => set("sku", e.target.value)} className={inputCls} data-testid="pf-sku" /></div>
              <div><label className={labelCls}>Ürün Kodu</label><input value={form.product_code || ""} onChange={(e) => set("product_code", e.target.value)} className={inputCls} data-testid="pf-code" /></div>
              <div><label className={labelCls}>Barkod</label><input value={form.barcode || ""} onChange={(e) => set("barcode", e.target.value)} className={inputCls} data-testid="pf-barcode" /></div>
            </div>
            <div><label className={labelCls}>Kısa Açıklama</label>
              <input value={form.short_description || ""} onChange={(e) => set("short_description", e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>Açıklama</label>
              <textarea rows={4} value={form.description || ""} onChange={(e) => set("description", e.target.value)} className={`${inputCls} py-3 resize-none`} data-testid="pf-description" /></div>
            <div><label className={labelCls}>Görsel URL'leri (her satıra bir tane)</label>
              <textarea rows={3} value={imagesText} onChange={(e) => setImagesText(e.target.value)} className={`${inputCls} py-3 font-mono-ed text-xs resize-none`} placeholder="https://..." data-testid="pf-images" /></div>
            <div><label className={labelCls}>Etiketler (virgülle)</label>
              <input value={tagsText} onChange={(e) => setTagsText(e.target.value)} className={inputCls} /></div>
          </section>

          <section className="bg-[#181B20] border border-[#272B33] rounded-2xl p-6">
            <label className="flex items-center gap-2.5 text-sm text-white mb-4 cursor-pointer">
              <input type="checkbox" checked={form.has_variants} onChange={(e) => set("has_variants", e.target.checked)} className="accent-blue-500 w-4 h-4" data-testid="pf-has-variants" />
              Bu ürün varyantlı (Renk, Beden vb.)
            </label>
            {form.has_variants && (
              <div className="space-y-4">
                {form.variant_options.map((opt, i) => (
                  <div key={i} className="flex gap-3 items-end">
                    <div className="w-40"><label className={labelCls}>Varyant Tipi</label>
                      <input value={opt.name} onChange={(e) => { const o = [...form.variant_options]; o[i] = { ...o[i], name: e.target.value }; set("variant_options", o); }} placeholder="Renk" className={inputCls} data-testid={`pf-option-name-${i}`} /></div>
                    <div className="flex-1"><label className={labelCls}>Değerler (virgülle)</label>
                      <input defaultValue={opt.values.join(", ")} onBlur={(e) => setOptionValues(i, e.target.value)} placeholder="Siyah, Beyaz, Lacivert" className={inputCls} data-testid={`pf-option-values-${i}`} /></div>
                    <button type="button" onClick={() => set("variant_options", form.variant_options.filter((_, x) => x !== i))} className="p-2.5 text-neutral-500 hover:text-rose-400" aria-label="Sil"><Trash2 size={16} /></button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <button type="button" onClick={() => set("variant_options", [...form.variant_options, { name: "", values: [] }])}
                    className="h-10 px-4 rounded-xl border border-[#272B33] text-sm text-neutral-300 hover:border-blue-500 flex items-center gap-2" data-testid="pf-add-option">
                    <Plus size={15} /> Varyant Tipi Ekle
                  </button>
                  <button type="button" onClick={generateMatrix}
                    className="h-10 px-4 rounded-xl bg-blue-600/15 text-blue-400 text-sm font-semibold hover:bg-blue-600/25 flex items-center gap-2" data-testid="pf-generate-matrix">
                    <Wand2 size={15} /> Kombinasyonları Oluştur
                  </button>
                </div>

                {variants.length > 0 && (
                  <div className="overflow-x-auto rounded-xl border border-[#272B33] mt-4">
                    <table className="w-full text-xs min-w-[860px]" data-testid="variant-matrix">
                      <thead><tr className="text-left text-neutral-500 border-b border-[#272B33] bg-[#0F1115]">
                        <th className="px-3 py-2.5">Varyant</th><th className="px-3 py-2.5">SKU</th><th className="px-3 py-2.5">Barkod</th><th className="px-3 py-2.5">Fiyat</th><th className="px-3 py-2.5">İnd. Fiyat</th><th className="px-3 py-2.5">Stok</th><th className="px-3 py-2.5">Kritik Eşik</th><th className="px-3 py-2.5">Görsel URL</th><th className="px-3 py-2.5">Aktif</th>
                      </tr></thead>
                      <tbody>
                        {variants.map((v, i) => (
                          <tr key={i} className="border-b border-[#272B33]/50" data-testid={`variant-row-${i}`}>
                            <td className="px-3 py-2 text-white font-medium whitespace-nowrap">{Object.entries(v.option_values).map(([k, val]) => `${k}: ${val}`).join(" · ")}</td>
                            {[["sku", "text"], ["barcode", "text"], ["price", "number"], ["sale_price", "number"], ["stock", "number"], ["low_stock_threshold", "number"], ["image_url", "text"]].map(([field, type]) => (
                              <td key={field} className="px-2 py-1.5">
                                <input value={v[field] ?? ""} type={type}
                                  onChange={(e) => { const vs = [...variants]; vs[i] = { ...vs[i], [field]: e.target.value }; setVariants(vs); }}
                                  className="w-full min-w-[80px] h-9 px-2.5 bg-[#0F1115] border border-[#272B33] rounded-lg text-white focus:outline-none focus:border-blue-500"
                                  data-testid={`variant-${field}-${i}`} />
                              </td>
                            ))}
                            <td className="px-3 py-2"><input type="checkbox" checked={!!v.active} onChange={(e) => { const vs = [...variants]; vs[i] = { ...vs[i], active: e.target.checked }; setVariants(vs); }} className="accent-blue-500 w-4 h-4" data-testid={`variant-active-${i}`} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </section>

          <section className="bg-[#181B20] border border-[#272B33] rounded-2xl p-6 space-y-4">
            <h2 className="font-semibold text-white text-sm">SEO</h2>
            <div><label className={labelCls}>SEO Başlığı</label><input value={form.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>SEO Açıklaması</label><textarea rows={2} value={form.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} className={`${inputCls} py-3 resize-none`} /></div>
            <div><label className={labelCls}>Anahtar Kelimeler</label><input value={form.seo_keywords || ""} onChange={(e) => set("seo_keywords", e.target.value)} className={inputCls} /></div>
          </section>
        </div>

        <div className="space-y-6">
          <section className="bg-[#181B20] border border-[#272B33] rounded-2xl p-6 space-y-4">
            <h2 className="font-semibold text-white text-sm">Fiyat & Stok</h2>
            <div><label className={labelCls}>Satış Fiyatı (₺) *</label><input required type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} className={inputCls} data-testid="pf-price" /></div>
            <div><label className={labelCls}>İndirimli Fiyat (₺)</label><input type="number" step="0.01" value={form.sale_price ?? ""} onChange={(e) => set("sale_price", e.target.value)} className={inputCls} data-testid="pf-sale-price" /></div>
            <div><label className={labelCls}>Alış Fiyatı (₺)</label><input type="number" step="0.01" value={form.purchase_price ?? ""} onChange={(e) => set("purchase_price", e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>KDV Oranı (%)</label><input type="number" value={form.vat_rate} onChange={(e) => set("vat_rate", e.target.value)} className={inputCls} data-testid="pf-vat" /></div>
            {!form.has_variants && <div><label className={labelCls}>Stok</label><input type="number" value={form.stock} onChange={(e) => set("stock", e.target.value)} className={inputCls} data-testid="pf-stock" /></div>}
            <div><label className={labelCls}>Kritik Stok Seviyesi</label><input type="number" value={form.critical_stock} onChange={(e) => set("critical_stock", e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>Güvenlik Stoğu (marketplace'e ayrılmaz)</label><input type="number" value={form.safety_stock ?? 0} onChange={(e) => set("safety_stock", e.target.value)} className={inputCls} data-testid="pf-safety-stock" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Desi</label><input type="number" step="0.1" value={form.desi ?? ""} onChange={(e) => set("desi", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Ağırlık (kg)</label><input type="number" step="0.01" value={form.weight ?? ""} onChange={(e) => set("weight", e.target.value)} className={inputCls} /></div>
            </div>
            <div><label className={labelCls}>Kargo Rozeti (örn. Aynı Gün Kargo)</label><input value={form.shipping_badge || ""} onChange={(e) => set("shipping_badge", e.target.value)} className={inputCls} data-testid="pf-shipping-badge" /></div>
            <div><label className={labelCls}>Kargo Mesajı</label><input value={form.shipping_message || ""} onChange={(e) => set("shipping_message", e.target.value)} className={inputCls} data-testid="pf-shipping-message" /></div>
          </section>

          <section className="bg-[#181B20] border border-[#272B33] rounded-2xl p-6 space-y-4">
            <h2 className="font-semibold text-white text-sm">Organizasyon</h2>
            <div><label className={labelCls}>Kategori</label>
              <select value={form.category_id || ""} onChange={(e) => set("category_id", e.target.value)} className={inputCls} data-testid="pf-category">
                <option value="">Seçin</option>
                {roots.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
              </select></div>
            <div><label className={labelCls}>Alt Kategori</label>
              <select value={form.subcategory_id || ""} onChange={(e) => set("subcategory_id", e.target.value)} className={inputCls} data-testid="pf-subcategory">
                <option value="">Seçin</option>
                {subs.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
              </select></div>
            <div><label className={labelCls}>Marka</label>
              <select value={form.brand_id || ""} onChange={(e) => set("brand_id", e.target.value)} className={inputCls} data-testid="pf-brand">
                <option value="">Seçin</option>
                {brands.map((b) => <option key={b.brand_id} value={b.brand_id}>{b.name}</option>)}
              </select></div>
            <div className="space-y-2.5 pt-2">
              {[["active", "Aktif (satışta)"], ["is_featured", "Öne çıkan"], ["is_new", "Yeni ürün"], ["requires_quote", "Teklif ile satılır (sepete eklenemez)"], ["customizable", "Kişiselleştirilebilir (fotoğraf/metin yükleme)"]].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2.5 text-sm text-neutral-300 cursor-pointer">
                  <input type="checkbox" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} className="accent-blue-500 w-4 h-4" data-testid={`pf-${k}`} /> {label}
                </label>
              ))}
            </div>
          </section>
        </div>
      </div>
    </form>
  );
}
